import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../lib/auth";
import { useCardsStore } from "../lib/store";
import {
  autoMapColumns,
  convertRowsToCards,
  FIELD_LABELS,
  parseCsvFile,
  type MappableField,
  type ParsedCSV,
} from "../lib/csv";
import { appendCards, detectTsubameSheet, ensureTagsColumn } from "../lib/sheets";
import { IMPORT_MAX_ROWS } from "../lib/constants";
import type { ColumnMap } from "../lib/types";

type Step = "upload" | "mapping" | "running" | "done" | "error";

export default function CsvImportFlow() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const sheet = useCardsStore((s) => s.sheet);
  const setSheet = useCardsStore((s) => s.setSheet);
  const setStatus = useCardsStore((s) => s.setStatus);

  const [step, setStep] = useState<Step>("upload");
  const [parsed, setParsed] = useState<ParsedCSV | null>(null);
  const [columnMap, setColumnMap] = useState<ColumnMap>({});
  const [error, setError] = useState<string | null>(null);
  const [appended, setAppended] = useState(0);
  const [bulkTagsInput, setBulkTagsInput] = useState("");
  const [fillTodayIfEmpty, setFillTodayIfEmpty] = useState(true);

  const handleFile = async (file: File) => {
    setError(null);
    try {
      const result = await parseCsvFile(file);
      if (result.rows.length === 0) {
        setError("CSV にデータ行がありません");
        return;
      }
      if (result.rows.length > IMPORT_MAX_ROWS) {
        setError(
          `インポート可能件数の上限(${IMPORT_MAX_ROWS}件)を超えています(${result.rows.length}件)`,
        );
        return;
      }
      setParsed(result);
      setColumnMap(autoMapColumns(result.headers));
      setStep("mapping");
    } catch (err) {
      setError(err instanceof Error ? err.message : "CSV 読み込みに失敗しました");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  /** CSV ヘッダを起点に「このヘッダ → このフィールド」を切り替える */
  const updateMappingByHeader = (header: string, field: MappableField | "") => {
    setColumnMap((prev) => {
      const next = { ...prev };
      // 既存:この header を value にしている key を全部剥がす
      for (const [k, v] of Object.entries(next)) {
        if (v === header) {
          delete (next as Record<string, string>)[k];
        }
      }
      // 既存:この field の旧マッピングは上書き(自動で消える)
      if (field !== "") {
        (next as Record<string, string>)[field] = header;
      }
      return next;
    });
  };

  const runImport = async () => {
    if (!parsed || !accessToken) return;
    setStep("running");
    setError(null);

    try {
      // シート未取得なら検出
      let target = sheet;
      if (!target) {
        const detected = await detectTsubameSheet(accessToken);
        if (!detected) {
          throw new Error(
            "つばめカードのスプレッドシートが見つかりません。先に iOS 版でシートを作成してください。",
          );
        }
        target = detected;
        setSheet(detected);
      }

      await ensureTagsColumn(target.id, accessToken).catch(() => {});

      const bulkTags = bulkTagsInput
        .split(/[,、，;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const cards = convertRowsToCards(parsed.rows, columnMap, {
        bulkTags,
        fillTodayIfEmpty,
      });
      const { appended: count } = await appendCards(
        target.id,
        cards,
        accessToken,
      );
      setAppended(count);
      // 一覧の再読込トリガー(次回 /cards 訪問時に再フェッチ)
      setStatus("idle");
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "インポートに失敗しました");
      setStep("error");
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (step === "upload") {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="rounded-lg border-2 border-dashed border-washiDeep bg-white px-6 py-16 text-center"
      >
        <p className="text-sm font-medium text-navy">
          CSV ファイルをドロップ
        </p>
        <p className="mt-1 text-xs text-inkLight">または下のボタンから選択</p>
        <label className="btn-primary mt-6 inline-block cursor-pointer">
          ファイルを選択
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleSelect}
            className="hidden"
          />
        </label>
        {error && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <p className="mt-8 text-xs text-inkLight">
          対応ファイル: UTF-8 / Shift-JIS の CSV(1行目=ヘッダ)<br />
          上限: {IMPORT_MAX_ROWS.toLocaleString()} 件
        </p>
      </div>
    );
  }

  if (step === "mapping" && parsed) {
    const previewRows = parsed.rows.slice(0, 5);
    // CSV ヘッダ → 割当フィールドの逆引き
    const headerToField: Record<string, MappableField | undefined> = {};
    for (const [field, header] of Object.entries(columnMap)) {
      if (header) headerToField[header] = field as MappableField;
    }
    const mappedCount = parsed.headers.filter((h) => headerToField[h]).length;
    const usedFields = new Set(Object.keys(columnMap));

    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-washiDeep bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-navy">列の紐付け</h3>
            <span className="text-xs text-inkLight">
              {parsed.headers.length} 列中 <strong className="text-navy">{mappedCount}</strong> 列を紐付け済 · 残りはメモに集約
            </span>
          </div>
          <p className="mt-1 text-xs text-inkLight">
            CSV の各列を、つばめカードのどの項目に入れるかを選んでください。
            <br />
            「— メモに集約 —」を選ぶと、その列の値はメモ列にまとめて記録されます。
          </p>

          <div className="mt-4 space-y-2">
            {parsed.headers.map((header) => {
              const matched = headerToField[header];
              const sampleValue = parsed.rows[0]?.[header] ?? "";
              return (
                <div
                  key={header}
                  className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                    matched
                      ? "border-emerald-200 bg-emerald-50/40"
                      : "border-washiDeep bg-washi/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-navy">{header}</span>
                      {matched ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                          自動で紐付け
                        </span>
                      ) : (
                        <span className="rounded-full bg-washiDeep px-2 py-0.5 text-[10px] font-medium text-inkLight">
                          メモに集約
                        </span>
                      )}
                    </div>
                    {sampleValue && (
                      <p className="mt-0.5 truncate text-xs text-inkLight" title={sampleValue}>
                        例: {sampleValue}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-inkLight">→</span>
                  <select
                    value={matched ?? ""}
                    onChange={(e) =>
                      updateMappingByHeader(header, e.target.value as MappableField | "")
                    }
                    className="w-44 shrink-0 rounded-md border border-washiDeep bg-white px-2 py-1 text-sm focus:border-navy focus:outline-none"
                  >
                    <option value="">— メモに集約 —</option>
                    {(Object.keys(FIELD_LABELS) as MappableField[]).map((f) => {
                      const isTaken = usedFields.has(f) && matched !== f;
                      return (
                        <option key={f} value={f} disabled={isTaken}>
                          {FIELD_LABELS[f]}
                          {isTaken ? "(他列で使用中)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-washiDeep bg-white p-4">
          <h3 className="text-sm font-medium text-navy">プレビュー(先頭 5 行)</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-washiDeep/60">
                <tr>
                  {parsed.headers.map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-2 py-1 text-left font-medium text-navy"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-washiDeep/50">
                {previewRows.map((r, i) => (
                  <tr key={i}>
                    {parsed.headers.map((h) => (
                      <td
                        key={h}
                        className="truncate whitespace-nowrap px-2 py-1 text-navySoft"
                        title={r[h] ?? ""}
                      >
                        {r[h] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-inkLight">
            読込件数: {parsed.rows.length.toLocaleString()} 件
            {parsed.errors.length > 0 && ` · 警告 ${parsed.errors.length} 件`}
          </p>
        </div>

        <div className="rounded-lg border border-washiDeep bg-white p-4">
          <h3 className="text-sm font-medium text-navy">インポートオプション</h3>

          <div className="mt-4 space-y-4">
            <div>
              <label
                htmlFor="csv-bulk-tags"
                className="block text-xs font-medium text-navySoft"
              >
                すべての行に追加するタグ(任意)
              </label>
              <input
                id="csv-bulk-tags"
                type="text"
                value={bulkTagsInput}
                onChange={(e) => setBulkTagsInput(e.target.value)}
                placeholder="例: 移行データ, Eight, 2026年4月"
                className="mt-1 w-full rounded-md border border-washiDeep bg-white px-3 py-2 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy/30"
              />
              <p className="mt-1 text-[11px] text-inkLight">
                カンマ・空白・セミコロンで複数指定。CSV 内のタグ列とマージされます。
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={fillTodayIfEmpty}
                onChange={(e) => setFillTodayIfEmpty(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-washiDeep text-navy focus:ring-navy/30"
              />
              <div>
                <span className="font-medium text-navy">取得日が空の時は今日の日付を入れる</span>
                <p className="text-[11px] text-inkLight">
                  オフにすると、取得日列がない CSV では取得日は空のままになります。
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setParsed(null);
              setStep("upload");
            }}
            className="btn-secondary"
          >
            ファイルを選び直す
          </button>
          <button type="button" onClick={runImport} className="btn-primary">
            {parsed.rows.length} 件をインポート
          </button>
        </div>
      </div>
    );
  }

  if (step === "running") {
    return (
      <div className="px-6 py-16 text-center text-sm text-navySoft">
        Google Sheets に書き込んでいます...
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="rounded-lg border border-washiDeep bg-white px-6 py-12 text-center">
        <p className="text-lg font-medium text-navy">
          ✅ {appended.toLocaleString()} 件のインポートが完了しました
        </p>
        <p className="mt-2 text-xs text-inkLight">
          シートの末尾に追加されました。
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              setParsed(null);
              setColumnMap({});
              setBulkTagsInput("");
              setAppended(0);
              setStep("upload");
            }}
            className="btn-secondary"
          >
            続けてインポート
          </button>
          {sheet && (
            <a
              href={`https://docs.google.com/spreadsheets/d/${sheet.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Google Sheets で確認 ↗
            </a>
          )}
          <button
            type="button"
            onClick={() => navigate("/cards")}
            className="btn-primary"
          >
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-12 text-center">
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => setStep("mapping")}
            className="btn-secondary"
          >
            戻る
          </button>
          <button type="button" onClick={runImport} className="btn-primary">
            再試行
          </button>
        </div>
      </div>
    );
  }

  return null;
}
